import {
  Button as KitButton,
  Card as KitCard,
  Container,
  Field as KitField,
  Input,
  Notice as KitNotice,
  SectionHeading,
  type ButtonProps,
} from '@growup/ui';
import type { InputHTMLAttributes, ReactElement, ReactNode } from 'react';
import { Link } from 'react-router-dom';

/**
 * The account pages' building blocks, on top of the Grow uP web kit
 * (`@growup/ui`). Only the choices the account pages repeat live here; the
 * look itself is the kit's.
 */

/** `primary` for the one main action of a form; `secondary` for the rest. */
export function Button({
  tone = 'default',
  ...props
}: Omit<ButtonProps, 'variant'> & { tone?: 'default' | 'primary' | 'quiet' }) {
  const variant = { default: 'secondary', primary: 'primary', quiet: 'ghost' } as const;
  return <KitButton variant={variant[tone]} {...props} />;
}

export function TextLink({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link to={to} className="gu-link">
      {children}
    </Link>
  );
}

export function Card({
  title,
  children,
  actions,
}: {
  title?: string;
  children: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <KitCard title={title} actions={actions} headingLevel={2}>
      {children}
    </KitCard>
  );
}

/** Form and API errors: the one place the site uses the danger colour. */
export function ErrorNote({ message }: { message: string | null }) {
  if (!message) return null;
  return <KitNotice tone="danger">{message}</KitNotice>;
}

/**
 * Neutral note. The kit keeps status colours for the back-office, so on the
 * site a note says what happened in words, under a short title when it matters.
 */
export function Notice({ title, children }: { title?: string; children?: ReactNode }) {
  return (
    <KitNotice tone="neutral" {...(title ? { title } : {})}>
      {children}
    </KitNotice>
  );
}

export function Field({
  label,
  children,
  hint,
  error,
  optional,
}: {
  label: string;
  children: ReactElement;
  hint?: string | undefined;
  error?: string | undefined;
  optional?: boolean;
}) {
  return (
    <KitField label={label} hint={hint} error={error} {...(optional ? { optional } : {})}>
      {children}
    </KitField>
  );
}

export function TextInput(props: InputHTMLAttributes<HTMLInputElement>) {
  return <Input {...props} />;
}

export { PasswordInput } from '@growup/ui';

/** Narrow column, centred on the screen, for the sign-in and email-link pages. */
export function NarrowPage({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Container className="py-12">
      <div className="mx-auto flex w-full max-w-lg flex-col gap-6">
        <SectionHeading level={1} size="m" title={title} />
        {children}
      </div>
    </Container>
  );
}

/** Page title inside the account area. */
export function PageTitle({ children, lead }: { children: ReactNode; lead?: ReactNode }) {
  return <SectionHeading level={1} size="m" title={children} lead={lead} />;
}

/** Barra de progresso simples (a checklist usa-a tambem). */
export function ProgressBar({ done, total }: { done: number; total: number }) {
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  return (
    <div
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={total}
      aria-valuenow={done}
      aria-label={`${String(done)} de ${String(total)}`}
      className="my-1 h-2 w-full overflow-hidden rounded-full bg-gu-sunken"
    >
      <div className="h-full bg-gu-accent" style={{ width: `${String(pct)}%` }} />
    </div>
  );
}
